import type { CampaignState } from '../types/game';
import type { DataRegistry } from './DataRegistry';
import { GameState } from '../game/GameState';

type Pair = [string, string];

interface ScenarioScript {
  wars?: Pair[];
  alliances?: Pair[];
  trade?: Pair[];
  nonAggression?: Pair[];
  marriages?: Pair[];
  embargoes?: Pair[];
  techs?: Record<string, string[]>;
  claims?: Record<string, string[]>;
  events?: Array<{
    id: string;
    turn: number;
    title: string;
    effect: (campaign: CampaignState) => void;
  }>;
}

const scripts: Record<string, ScenarioScript> = {
  '1293_majapahit': {
    wars: [['majapahit', 'ming']],
    trade: [['majapahit', 'arab_confederation'], ['sunda', 'malacca']],
    claims: { majapahit: ['trowulan', 'surabaya', 'demak'] },
    events: [
      { id: 'yuan_withdrawal', turn: 5, title: 'Foreign expedition loses momentum', effect: (campaign) => { campaign.factions.majapahit.prestige += 4; campaign.factions.ming.warExhaustion += 8; } },
    ],
  },
  '1478_fracture': {
    wars: [['majapahit', 'demak'], ['majapahit', 'mataram']],
    alliances: [['demak', 'cirebon']],
    trade: [['demak', 'banten'], ['malacca', 'arab_confederation']],
    claims: { demak: ['demak', 'surabaya'], mataram: ['mataram', 'trowulan'], majapahit: ['trowulan', 'surabaya'] },
    events: [
      { id: 'court_schism', turn: 4, title: 'Court schism spreads through Java', effect: (campaign) => { campaign.factions.majapahit.legitimacy -= 8; campaign.provinces.surabaya.unrest += 6; } },
    ],
  },
  '1511_malacca_falls': {
    wars: [['portuguese', 'malacca'], ['aceh', 'portuguese']],
    trade: [['demak', 'cirebon'], ['banten', 'arab_confederation']],
    embargoes: [['malacca', 'portuguese']],
    techs: { portuguese: ['maritime_cartography', 'gunpowder_drill'], malacca: ['maritime_cartography'], aceh: ['maritime_cartography'] },
    claims: { portuguese: ['portuguese_outpost', 'malacca'], malacca: ['malacca', 'portuguese_outpost'] },
    events: [
      { id: 'malacca_refugees', turn: 3, title: 'Malacca merchants seek new harbors', effect: (campaign) => { campaign.provinces.demak.storage.spices = (campaign.provinces.demak.storage.spices ?? 0) + 8; campaign.provinces.banten.storage.spices = (campaign.provinces.banten.storage.spices ?? 0) + 8; } },
    ],
  },
  '1595_foreign_fleets': {
    wars: [['dutch_voc', 'portuguese']],
    trade: [['dutch_voc', 'banten'], ['banten', 'cirebon'], ['malacca', 'arab_confederation']],
    techs: { portuguese: ['maritime_cartography', 'gunpowder_drill'], dutch_voc: ['maritime_cartography', 'gunpowder_drill', 'court_records'], banten: ['maritime_cartography'] },
    claims: { dutch_voc: ['banten', 'portuguese_outpost'], portuguese: ['portuguese_outpost', 'malacca'] },
    events: [
      { id: 'company_factors', turn: 4, title: 'Foreign factors bid for spice contracts', effect: (campaign) => { campaign.factions.banten.treasury += 45; campaign.factions.dutch_voc.prestige += 3; } },
    ],
  },
  '1628_contest_for_java': {
    wars: [['mataram', 'demak'], ['mataram', 'majapahit'], ['banten', 'portuguese']],
    alliances: [['demak', 'cirebon'], ['blambangan', 'bali']],
    trade: [['banten', 'arab_confederation'], ['malacca', 'aceh']],
    techs: { mataram: ['wet_rice_administration', 'supply_roads'], portuguese: ['maritime_cartography', 'gunpowder_drill'], banten: ['maritime_cartography'], demak: ['wet_rice_administration'] },
    claims: { mataram: ['mataram', 'demak', 'trowulan', 'surabaya'], demak: ['demak', 'cirebon'], banten: ['banten', 'portuguese_outpost'] },
    events: [
      { id: 'royal_siege_preparations', turn: 3, title: 'Royal siege preparations gather labor', effect: (campaign) => { campaign.factions.mataram.treasury += 35; campaign.provinces.mataram.storage.rations = (campaign.provinces.mataram.storage.rations ?? 0) + 30; } },
    ],
  },
  '1700_company_power': {
    wars: [['dutch_voc', 'banten'], ['dutch_voc', 'mataram']],
    trade: [['dutch_voc', 'malacca'], ['british_eic', 'arab_confederation']],
    embargoes: [['banten', 'dutch_voc']],
    techs: { dutch_voc: ['maritime_cartography', 'gunpowder_drill', 'court_records', 'fortification_design'], british_eic: ['maritime_cartography', 'gunpowder_drill'], mataram: ['wet_rice_administration', 'supply_roads'] },
    claims: { dutch_voc: ['banten', 'portuguese_outpost', 'malacca'], british_eic: ['malacca', 'aceh'], mataram: ['mataram', 'surabaya'] },
    events: [
      { id: 'company_monopoly', turn: 6, title: 'Company monopoly contracts tighten', effect: (campaign) => { campaign.factions.dutch_voc.treasury += 60; campaign.factions.banten.warExhaustion += 5; } },
    ],
  },
};

export class ScenarioManager {
  static readonly instance = new ScenarioManager();

  applyInitialSetup(campaign: CampaignState, registry: DataRegistry) {
    const script = scripts[campaign.scenarioId];
    if (!script) return;
    for (const pair of script.wars ?? []) this.linkWar(campaign, pair[0], pair[1]);
    for (const pair of script.alliances ?? []) this.linkTreaty(campaign, 'alliances', pair[0], pair[1]);
    for (const pair of script.trade ?? []) this.linkTreaty(campaign, 'trade', pair[0], pair[1]);
    for (const pair of script.nonAggression ?? []) this.linkTreaty(campaign, 'nonAggression', pair[0], pair[1]);
    for (const pair of script.marriages ?? []) this.linkTreaty(campaign, 'marriages', pair[0], pair[1]);
    for (const pair of script.embargoes ?? []) this.linkTreaty(campaign, 'embargoes', pair[0], pair[1]);
    for (const [factionId, techs] of Object.entries(script.techs ?? {})) {
      const faction = campaign.factions[factionId];
      if (!faction) continue;
      faction.researchedTechs = [...new Set([...(faction.researchedTechs ?? []), ...techs])];
    }
    for (const [factionId, claims] of Object.entries(script.claims ?? {})) {
      const faction = campaign.factions[factionId];
      if (!faction) continue;
      faction.claims = claims.filter((provinceId) => Boolean(registry.provinces[provinceId]));
    }
    GameState.instance.log(`Scenario setup applied: ${registry.scenarios[campaign.scenarioId]?.name ?? campaign.scenarioId}.`, 'info');
  }

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    const script = scripts[campaign.scenarioId];
    if (!script?.events?.length) return;
    for (const event of script.events) {
      const key = `scenario:${event.id}`;
      if (campaign.scenarioFlags[key] || campaign.turn < event.turn) continue;
      event.effect(campaign);
      campaign.scenarioFlags[key] = true;
      campaign.eventHistory.unshift({ id: event.id, title: event.title, turn: campaign.turn });
      campaign.eventHistory = campaign.eventHistory.slice(0, 20);
      GameState.instance.log(event.title, 'warning');
    }
  }

  private linkWar(campaign: CampaignState, a: string, b: string) {
    const left = campaign.factions[a];
    const right = campaign.factions[b];
    if (!left || !right) return;
    if (!left.atWarWith.includes(b)) left.atWarWith.push(b);
    if (!right.atWarWith.includes(a)) right.atWarWith.push(a);
  }

  private linkTreaty(campaign: CampaignState, treaty: string, a: string, b: string) {
    const left = campaign.factions[a];
    const right = campaign.factions[b];
    if (!left || !right) return;
    left.treaties[treaty] ??= [];
    right.treaties[treaty] ??= [];
    if (!left.treaties[treaty].includes(b)) left.treaties[treaty].push(b);
    if (!right.treaties[treaty].includes(a)) right.treaties[treaty].push(a);
  }
}
